'use client';

import { useMemo } from 'react';
import {
  usePageVariant,
  useHeadlineVariant,
  useCTAVariant,
  useSubheadVariant,
  type PageVariant,
  type HeadlineVariant,
  type CTAVariant,
  type SubheadVariant,
} from './posthogABTesting';
import {
  HEADLINE_CONTENT,
  CTA_CONTENT,
  SUBHEAD_CONTENT,
  type HeadlineContent,
  type CTAContent,
  type SubheadContent,
} from '../features/landing/content/variants';

/**
 * Landing Page Variant Hook
 *
 * Story 6.6: A/B Variant Refinement & Admin Analytics
 *
 * Combines PostHog feature flag hooks with content variants
 * to provide a unified interface for landing page A/B testing.
 *
 * @example
 * ```tsx
 * const {
 *   pageVariant,
 *   headline,
 *   cta,
 *   subhead,
 *   isLoading,
 *   variantSource,
 * } = useLandingVariants();
 *
 * // Use i18n keys for translation
 * const headlineText = t(headline.i18nKey);
 *
 * // Or use default English text
 * const headlineText = headline.text;
 * ```
 */
export interface UseLandingVariantsReturn {
  // Page layout variant
  pageVariant: PageVariant;

  // Component content variants
  headline: HeadlineContent;
  cta: CTAContent;
  subhead: SubheadContent;

  // Raw variant keys (for analytics)
  headlineVariant: HeadlineVariant;
  ctaVariant: CTAVariant;
  subheadVariant: SubheadVariant;

  // Loading state
  isLoading: boolean;

  // Source of page variant assignment
  variantSource: 'posthog' | 'cookie' | 'default';
}

/**
 * Hook to get all landing page variant content
 *
 * Returns resolved content objects with i18n keys, text, and angle metadata.
 * Uses PostHog feature flags for variant selection with cookie fallback.
 */
export function useLandingVariants(): UseLandingVariantsReturn {
  // Get variant assignments from PostHog
  const pageResult = usePageVariant();
  const headlineResult = useHeadlineVariant();
  const ctaResult = useCTAVariant();
  const subheadResult = useSubheadVariant();

  // Combined loading state
  const isLoading =
    pageResult.isLoading ||
    headlineResult.isLoading ||
    ctaResult.isLoading ||
    subheadResult.isLoading;

  // Memoize content lookups to avoid unnecessary re-renders
  const headline = useMemo(
    () => HEADLINE_CONTENT[headlineResult.variant],
    [headlineResult.variant]
  );

  const cta = useMemo(
    () => CTA_CONTENT[ctaResult.variant],
    [ctaResult.variant]
  );

  const subhead = useMemo(
    () => SUBHEAD_CONTENT[subheadResult.variant],
    [subheadResult.variant]
  );

  return {
    pageVariant: pageResult.variant,
    headline,
    cta,
    subhead,
    headlineVariant: headlineResult.variant,
    ctaVariant: ctaResult.variant,
    subheadVariant: subheadResult.variant,
    isLoading,
    variantSource: pageResult.source,
  };
}

/**
 * Get variant properties for analytics tracking
 *
 * Returns a flat object suitable for event properties.
 */
export function getVariantAnalyticsProperties(
  variants: Pick<
    UseLandingVariantsReturn,
    'pageVariant' | 'headlineVariant' | 'ctaVariant' | 'subheadVariant' | 'variantSource'
  >
): Record<string, string> {
  return {
    page_variant: variants.pageVariant,
    headline_variant: variants.headlineVariant,
    cta_variant: variants.ctaVariant,
    subhead_variant: variants.subheadVariant,
    variant_source: variants.variantSource,
  };
}
